import React, { useEffect, useState } from 'react'
import adminApi from './api'


interface Message {
  id: number
  name: string
  surname: string | null
  email: string
  message: string
  created_at: string
  ip_address: string | null
  user_agent: string | null
  is_read: boolean
}

export const MessagesAdmin: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null)
  const [modalOpen, setModalOpen] = useState(false)

  useEffect(() => {
    fetchMessages()
  }, [searchQuery])

  const fetchMessages = async () => {
    try {
      const params = new URLSearchParams()
      if (searchQuery) {
        params.append('search', searchQuery)
      }

      const { data } = await adminApi.get(`/api/admin/messages?${params.toString()}`)
      setMessages(data.messages)
    } catch (err) {
      // Error fetching messages
    } finally {
      setLoading(false)
    }
  }

  const handleRowClick = async (msg: Message) => {
    setSelectedMessage(msg)
    setModalOpen(true)

    // Mark as read if not already read
    if (!msg.is_read) {
      try {
        await adminApi.put(`/api/admin/messages/${msg.id}/read`, {})

        // Update local state
        setMessages(prevMessages =>
          prevMessages.map(m => m.id === msg.id ? { ...m, is_read: true } : m)
        )

        // Notify parent to update unread count
        window.dispatchEvent(new CustomEvent('messageRead'))
      } catch (err) {
        // Error marking message as read
      }
    }
  }

  const closeModal = () => {
    setModalOpen(false)
    setSelectedMessage(null)
  }

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Сообщения</h1>
        <p className="page-subtitle">Обращения пользователей</p>
      </div>

      {/* Search section */}
      <div className="filters-section">
        <div className="filter-group flex-grow">
          <label htmlFor="messageSearch" className="filter-label">Поиск</label>
          <input
            id="messageSearch"
            type="text"
            name="messageSearch"
            className="form-control"
            placeholder="Поиск по имени, email или сообщению..."
            value={searchQuery}
            onChange={handleSearch}
            autoComplete="off"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-20">
          <div className="loading-spinner w-10 h-10"></div>
        </div>
      ) : (
        <div className="table-card">
          <table className="admin-table">
            <thead>
              <tr>
                <th className="w-[200px]">Имя</th>
                <th>Сообщение</th>
                <th className="w-[180px]">Дата и время</th>
              </tr>
            </thead>
            <tbody>
              {messages.length === 0 ? (
                <tr>
                  <td colSpan={3}>
                    <div className="empty-state">
                      <div className="empty-state-text">Сообщения не найдены</div>
                    </div>
                  </td>
                </tr>
              ) : (
                messages.map(msg => (
                  <tr
                    key={msg.id}
                    onClick={() => handleRowClick(msg)}
                    className="cursor-pointer"
                  >
                    <td>
                      <div className="flex items-center gap-2">
                        {!msg.is_read && (
                          <div className="w-1 h-1 rounded-full bg-[#1890ff] flex-none" />
                        )}
                        <span>{msg.name} {msg.surname || ''}</span>
                      </div>
                    </td>
                    <td className="max-w-[400px] overflow-hidden text-ellipsis whitespace-nowrap">
                      {msg.message}
                    </td>
                    <td>
                      {new Date(msg.created_at).toLocaleString('ru-RU', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {modalOpen && selectedMessage && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Детали сообщения</h2>
              <button className="modal-close" onClick={closeModal}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="modal-field">
                <label className="modal-label">Имя:</label>
                <div className="modal-value">{selectedMessage.name} {selectedMessage.surname || ''}</div>
              </div>
              <div className="modal-field">
                <label className="modal-label">Email:</label>
                <div className="modal-value">{selectedMessage.email}</div>
              </div>
              <div className="modal-field">
                <label className="modal-label">Дата и время:</label>
                <div className="modal-value">
                  {new Date(selectedMessage.created_at).toLocaleString('ru-RU', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit'
                  })}
                </div>
              </div>
              <div className="modal-field">
                <label className="modal-label">Сообщение:</label>
                <div className="modal-value whitespace-pre-wrap">{selectedMessage.message}</div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-primary" onClick={closeModal}>Закрыть</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
